<template>
  <section class="audit-page">
    <div class="hero">
      <div>
        <h1>商品审核</h1>
        <p>用户在客户端发布后进入「待审核」，只有审核通过的商品才会出现在小程序的闲置商品列表。</p>
      </div>
      <el-button type="primary" icon="el-icon-refresh" size="small" :loading="loading" @click="reload">刷新列表</el-button>
    </div>

    <div class="stat-row">
      <div v-for="item in statCards" :key="item.key" class="stat-card" :class="item.key" @click="switchTab(item.tab)">
        <span class="stat-label">{{ item.label }}</span>
        <strong class="stat-value">{{ stats[item.key] }}</strong>
        <span class="stat-note">{{ item.note }}</span>
      </div>
    </div>

    <el-card shadow="never" class="table-card">
      <div class="toolbar">
        <div class="tabs">
          <span v-for="tab in tabs" :key="tab.value" :class="['tab', { active: statusFilter === tab.value }]" @click="switchTab(tab.value)">
            {{ tab.label }}<em v-if="tab.value === '待审核' && pendingCount">{{ pendingCount }}</em>
          </span>
        </div>
        <el-input v-model="keyword" clearable size="small" placeholder="搜索商品名称 / 卖家昵称" prefix-icon="el-icon-search" style="width: 280px" />
      </div>

      <el-table v-loading="loading" :data="pagedRows" border stripe empty-text="暂无商品">
        <el-table-column label="图片" width="96">
          <template slot-scope="scope">
            <div class="thumb-wrap" @click="openDetail(scope.row)">
              <img :src="scope.row.images[0]" class="thumb" />
              <span v-if="scope.row.images.length > 1" class="thumb-count">{{ scope.row.images.length }} 张</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="商品信息" min-width="240">
          <template slot-scope="scope">
            <div class="goods-name" @click="openDetail(scope.row)">{{ scope.row.name }}</div>
            <div class="goods-sub">ID: {{ scope.row.id }} · {{ scope.row.categoryName || '未分类' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="价格" width="100">
          <template slot-scope="scope">
            <span class="price">{{ scope.row.price > 0 ? '¥' + scope.row.price : '免费' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="conditionLevel" label="成色" width="100">
          <template slot-scope="scope">{{ scope.row.conditionLevel || '—' }}</template>
        </el-table-column>
        <el-table-column prop="tradeMode" label="交易方式" width="120">
          <template slot-scope="scope">{{ scope.row.tradeMode }}</template>
        </el-table-column>
        <el-table-column label="卖家" width="140">
          <template slot-scope="scope">
            <div>{{ scope.row.seller.name }}</div>
            <div class="goods-sub">{{ scope.row.seller.phone || '未留电话' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="发布时间" width="160">
          <template slot-scope="scope">{{ formatTime(scope.row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="110">
          <template slot-scope="scope">
            <el-tag :type="statusTag(scope.row.status)" size="small">{{ scope.row.status }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="openDetail(scope.row)">查看详情</el-button>
            <template v-if="scope.row.status === '待审核'">
              <el-button type="text" class="approve" @click="approve(scope.row)">通过</el-button>
              <el-button type="text" class="reject" @click="openReject(scope.row)">驳回</el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next"
        :total="filteredRows.length"
        :page-size="pageSize"
        :current-page.sync="page"
      />
    </el-card>

    <!-- 商品详情：完整展示发布时填写的所有字段 -->
    <el-dialog title="商品详情（发布信息全览）" :visible.sync="detailVisible" width="760px" top="6vh">
      <div v-if="current" class="detail">
        <div class="gallery">
          <img
            v-for="(img, index) in current.images"
            :key="index"
            :src="img"
            @click="previewImg(current.images, index)"
          />
          <div v-if="!current.images.length" class="no-image">该商品没有图片</div>
        </div>
        <div class="detail-grid">
          <div class="kv"><span class="k">商品名称</span><span class="v strong">{{ current.name }}</span></div>
          <div class="kv"><span class="k">价格</span><span class="v price">{{ current.price > 0 ? '¥' + current.price : '免费赠送' }}</span></div>
          <div class="kv"><span class="k">分类</span><span class="v">{{ current.categoryName || '未分类' }}</span></div>
          <div class="kv"><span class="k">成色</span><span class="v">{{ current.conditionLevel || '未填写' }}</span></div>
          <div class="kv"><span class="k">交易方式</span><span class="v">{{ current.tradeMode }}</span></div>
          <div class="kv"><span class="k">交易地点</span><span class="v">{{ current.address || '未填写' }}</span></div>
          <div class="kv"><span class="k">图片数量</span><span class="v">{{ current.images.length }} 张</span></div>
          <div class="kv"><span class="k">商品 ID</span><span class="v">{{ current.id }}</span></div>
          <div class="kv"><span class="k">发布时间</span><span class="v">{{ formatTime(current.createdAt) }}</span></div>
          <div class="kv"><span class="k">当前状态</span><span class="v"><el-tag :type="statusTag(current.status)" size="mini">{{ current.status }}</el-tag></span></div>
          <div class="kv"><span class="k">卖家昵称</span><span class="v">{{ current.seller.name }}</span></div>
          <div class="kv"><span class="k">卖家手机号</span><span class="v">{{ current.seller.phone || '未填写' }}</span></div>
          <div class="kv"><span class="k">卖家 ID</span><span class="v">{{ current.seller.id }}</span></div>
          <div class="kv"><span class="k">想要人数</span><span class="v">{{ current.wantCount || 0 }} 人</span></div>
          <div class="kv full"><span class="k">商品描述</span><span class="v desc">{{ current.description || '未填写' }}</span></div>
          <div v-if="current.rejectReason" class="kv full"><span class="k">驳回原因</span><span class="v reject-text">{{ current.rejectReason }}</span></div>
        </div>
      </div>
      <span slot="footer">
        <el-button v-if="current && current.status === '待审核'" type="danger" plain size="small" @click="openReject(current)">驳回</el-button>
        <el-button v-if="current && current.status === '待审核'" type="primary" size="small" @click="approve(current)">通过审核，上架到闲置列表</el-button>
        <el-button size="small" @click="detailVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 驳回原因 -->
    <el-dialog title="驳回商品" :visible.sync="rejectVisible" width="480px" @closed="rejectReason = ''">
      <p class="reject-tip">驳回后商品状态变为「审核不通过」，用户可在「我的发布」看到原因并修改后重新提交。<b>驳回原因必填。</b></p>
      <div class="quick-reasons">
        <span v-for="reason in quickReasons" :key="reason" class="chip" @click="rejectReason = reason">{{ reason }}</span>
      </div>
      <el-input v-model="rejectReason" type="textarea" :rows="3" maxlength="200" show-word-limit placeholder="请填写驳回原因（必填，会展示给卖家）" />
      <span slot="footer">
        <el-button size="small" @click="rejectVisible = false">取消</el-button>
        <el-button type="danger" size="small" :loading="reviewing" @click="confirmReject">确认驳回</el-button>
      </span>
    </el-dialog>
  </section>
</template>

<script>
import request from '@/api/request';

export default {
  data() {
    return {
      loading: false,
      reviewing: false,
      rows: [],
      stats: { pending: 0, onSale: 0, rejected: 0, offline: 0, sold: 0, total: 0 },
      keyword: '',
      statusFilter: '待审核',
      page: 1,
      pageSize: 10,
      detailVisible: false,
      current: null,
      rejectVisible: false,
      rejectTarget: null,
      rejectReason: '',
      tabs: [
        { label: '待审核', value: '待审核' },
        { label: '全部商品', value: '' },
        { label: '已通过', value: '在售' },
        { label: '审核不通过', value: '审核不通过' }
      ],
      quickReasons: ['图片不清晰或与实物不符', '描述信息不完整', '疑似违禁物品', '价格明显异常', '重复发布']
    };
  },
  computed: {
    statCards() {
      return [
        { key: 'pending', label: '待审核', note: '点击处理', tab: '待审核' },
        { key: 'onSale', label: '在售中', note: '已展示在小程序', tab: '在售' },
        { key: 'rejected', label: '审核不通过', note: '可查看原因', tab: '审核不通过' },
        { key: 'total', label: '商品总数', note: '不含已删除', tab: '' }
      ];
    },
    pendingCount() {
      return this.rows.filter((item) => item.status === '待审核').length;
    },
    filteredRows() {
      const key = this.keyword.trim().toLowerCase();
      return this.rows.filter((item) => {
        const matchStatus = !this.statusFilter || item.status === this.statusFilter;
        const matchKeyword = !key || `${item.name}${item.seller.name}`.toLowerCase().includes(key);
        return matchStatus && matchKeyword;
      });
    },
    pagedRows() {
      return this.filteredRows.slice((this.page - 1) * this.pageSize, this.page * this.pageSize);
    }
  },
  watch: {
    keyword() {
      this.page = 1;
    }
  },
  created() {
    this.reload();
  },
  methods: {
    async reload() {
      this.loading = true;
      try {
        const [summary, audit] = await Promise.all([
          request.get('/admin/catalog-summary'),
          request.get('/admin/goods/audit-summary')
        ]);
        this.rows = summary.data.goods || [];
        this.stats = audit.data || this.stats;
      } catch (error) {
        this.$message.error('审核列表加载失败，请重新登录后再试');
      } finally {
        this.loading = false;
      }
    },
    switchTab(value) {
      this.statusFilter = value;
      this.page = 1;
    },
    formatTime(value) {
      if (!value) return '—';
      const date = new Date(String(value).replace(' ', 'T'));
      if (Number.isNaN(date.getTime())) return String(value).slice(0, 19);
      const pad = (n) => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    statusTag(status) {
      if (status === '待审核') return 'warning';
      if (status === '在售') return 'success';
      if (status === '审核不通过') return 'danger';
      return 'info';
    },
    openDetail(row) {
      this.current = row;
      this.detailVisible = true;
    },
    previewImg(images, index) {
      if (this.$viewerApi) this.$viewerApi({ images, url: images[index] });
      else window.open(images[index]);
    },
    approve(row) {
      this.$confirm(`确认通过「${row.name}」的审核？通过后将立即展示在小程序的闲置商品列表。`, '审核通过', {
        type: 'info',
        confirmButtonText: '通过并上架'
      })
        .then(() => this.doReview(row, { action: 'approve' }))
        .catch(() => {});
    },
    openReject(row) {
      this.rejectTarget = row;
      this.rejectReason = '';
      this.rejectVisible = true;
    },
    async confirmReject() {
      if (!this.rejectTarget) return;
      const reason = String(this.rejectReason || '').trim();
      // 驳回原因必填：卖家要靠这个知道怎么改
      if (!reason) {
        this.$message.warning('请填写驳回原因');
        return;
      }
      await this.doReview(this.rejectTarget, { action: 'reject', reason });
      this.rejectVisible = false;
    },
    async doReview(row, payload) {
      if (this.reviewing) return;
      this.reviewing = true;
      try {
        await request.put(`/admin/goods/${row.id}/review`, payload);
        this.$message.success(payload.action === 'approve' ? '已通过审核，商品已上架' : '已驳回');
        this.detailVisible = false;
        await this.reload();
        this.page = 1;
      } catch (error) {
        this.$message.error((error.response && error.response.data && error.response.data.message) || '操作失败，请重试');
      } finally {
        this.reviewing = false;
      }
    }
  }
};
</script>

<style scoped>
.audit-page { padding: 0 4px; }
.hero { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 18px; }
.hero h1 { margin: 0; color: #24384b; font-size: 22px; font-weight: 600; }
.hero p { margin: 8px 0 0; color: #8493a1; font-size: 13px; }
.stat-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 16px; }
.stat-card { display: flex; flex-direction: column; gap: 6px; padding: 16px 18px; border-radius: 10px; background: #fff; border-left: 4px solid #d9e2ea; cursor: pointer; transition: box-shadow .2s; }
.stat-card:hover { box-shadow: 0 6px 18px rgba(36, 56, 75, .08); }
.stat-card.pending { border-left-color: #e6a23c; }
.stat-card.onSale { border-left-color: #2f9e6d; }
.stat-card.rejected { border-left-color: #e05d5d; }
.stat-card.total { border-left-color: #5b8ff9; }
.stat-label { color: #8493a1; font-size: 13px; }
.stat-value { color: #24384b; font-size: 26px; line-height: 1.2; font-weight: 700; }
.stat-note { color: #a6b2bd; font-size: 12px; }
.table-card { border-radius: 10px; }
.toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.tabs { display: flex; gap: 8px; }
.tab { padding: 7px 16px; border-radius: 16px; color: #5b6b78; background: #f2f5f8; font-size: 13px; cursor: pointer; }
.tab.active { color: #fff; background: #28745b; }
.tab em { margin-left: 6px; padding: 0 6px; border-radius: 9px; background: #e6a23c; color: #fff; font-size: 11px; font-style: normal; }
.thumb-wrap { position: relative; width: 64px; height: 50px; }
.thumb { width: 64px; height: 50px; object-fit: cover; border-radius: 4px; cursor: pointer; background: #f2f5f8; }
.thumb-count { position: absolute; right: 2px; bottom: 2px; padding: 0 4px; border-radius: 3px; background: rgba(0, 0, 0, .55); color: #fff; font-size: 10px; line-height: 15px; }
.goods-name { color: #24384b; font-weight: 600; cursor: pointer; }
.goods-name:hover { color: #28745b; }
.goods-sub { margin-top: 4px; color: #94a1ad; font-size: 12px; }
.price { color: #e05d5d; font-weight: 600; }
.approve { color: #28745b; font-weight: 600; }
.reject { color: #e05d5d; }
.pager { margin-top: 16px; text-align: right; }
.detail .gallery { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 18px; }
.detail .gallery img { width: 132px; height: 100px; object-fit: cover; border: 1px solid #ebeef5; border-radius: 6px; cursor: zoom-in; }
.detail .no-image { color: #a6b2bd; font-size: 13px; }
.detail-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0 24px; }
.detail .kv { display: flex; padding: 8px 0; border-bottom: 1px dashed #eef1f4; font-size: 13px; line-height: 1.7; }
.detail .kv.full { grid-column: span 2; }
.detail .k { flex: none; width: 88px; color: #8493a1; }
.detail .v { color: #24384b; word-break: break-all; }
.detail .v.strong { font-weight: 600; }
.detail .desc { white-space: pre-wrap; }
.detail .reject-text { color: #e05d5d; }
.reject-tip { margin: 0 0 12px; color: #8493a1; font-size: 13px; line-height: 1.6; }
.quick-reasons { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
.chip { padding: 4px 12px; border: 1px solid #e3e9ef; border-radius: 14px; color: #5b6b78; font-size: 12px; cursor: pointer; }
.chip:hover { border-color: #28745b; color: #28745b; }
</style>
